import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const MicrosoftCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [debugInfo, setDebugInfo] = useState<string[]>([]);
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const addDebug = (msg: string) => {
    console.log('[DEBUG]', msg);
    setDebugInfo(prev => [...prev, msg]);
  };

  useEffect(() => {
    const handleCallback = async () => {
      addDebug('Starting callback...');
      
      const code = searchParams.get('code');
      addDebug(`Code received: ${code ? 'YES' : 'NO'}`);
      
      if (!code) {
        addDebug('ERROR: No code in URL');
        setError('No authorization code');
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      try {
        addDebug('Sending code to backend...');
        const response = await axios.post(`${API_URL}/auth/microsoft/exchange`, { code });
        
        addDebug('Backend response received');
        addDebug(`Token: ${response.data.access_token ? 'YES' : 'NO'}`);
        addDebug(`User: ${response.data.user?.email || 'NONE'}`);
        addDebug(`Requires 2FA: ${response.data.user?.requires_2fa ? 'YES' : 'NO'}`);
        
        if (response.data.user?.requires_2fa) {
          // User has 2FA enabled - show 2FA input
          addDebug('2FA required - showing verification screen');
          setEmail(response.data.user.email);
          localStorage.setItem('temp_token', response.data.access_token);
          setRequires2FA(true);
        } else if (response.data.access_token) {
          addDebug('Saving token to localStorage...');
          localStorage.setItem('token', response.data.access_token);
          
          addDebug('Token saved. Redirecting to dashboard...');
          setTimeout(() => {
            window.location.href = '/dashboard';
          }, 1000);
        } else {
          addDebug('ERROR: No token in response');
          throw new Error('No token');
        }
      } catch (err: any) {
        addDebug(`ERROR: ${err.message}`);
        addDebug(`Response: ${JSON.stringify(err.response?.data)}`);
        console.error('Full error:', err);
        setError('Authentication failed: ' + err.message);
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  const handle2FAVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/auth/verify-2fa`, {
        email: email,
        code: twoFactorCode
      });

      localStorage.removeItem('temp_token');
      localStorage.setItem('token', response.data.access_token);
      window.location.href = '/dashboard';
    } catch (err: any) {
      setError(err.response?.data?.detail || '2FA verification failed.');
    } finally {
      setLoading(false);
    }
  };

  if (requires2FA) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: '#f5f7fa'
      }}>
        <div style={{
          background: 'white',
          padding: '40px',
          borderRadius: '8px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          maxWidth: '400px',
          width: '100%'
        }}>
          <h2>Two-Factor Authentication</h2>
          <p>Enter the code from your authenticator app</p>

          {error && <div style={{ 
            background: '#f8d7da', 
            color: '#721c24', 
            padding: '12px', 
            borderRadius: '4px',
            marginBottom: '15px'
          }}>{error}</div>}

          <form onSubmit={handle2FAVerification}>
            <input
              type="text"
              value={twoFactorCode}
              onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              maxLength={6}
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '24px',
                textAlign: 'center',
                letterSpacing: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                marginBottom: '15px'
              }}
              autoFocus
              required
            />

            <button
              type="submit"
              disabled={twoFactorCode.length !== 6 || loading}
              style={{
                width: '100%',
                padding: '12px',
                background: twoFactorCode.length === 6 ? '#667eea' : '#ccc',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '16px',
                cursor: twoFactorCode.length === 6 ? 'pointer' : 'not-allowed'
              }}
            >
              {loading ? 'Verifying...' : 'Verify'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      minHeight: '100vh',
      flexDirection: 'column',
      gap: '10px',
      padding: '20px',
      fontFamily: 'monospace'
    }}>
      {error ? (
        <>
          <h2 style={{ color: 'red' }}>❌ {error}</h2>
          <p>Redirecting to login...</p>
        </>
      ) : (
        <>
          <h2 style={{ color: 'green' }}>🔄 Processing...</h2>
          <p>Please wait...</p>
        </>
      )}
      
      <div style={{ 
        marginTop: '20px', 
        padding: '20px', 
        background: '#f5f5f5', 
        borderRadius: '8px',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '400px',
        overflow: 'auto'
      }}>
        <h3>Debug Log:</h3>
        {debugInfo.map((msg, i) => (
          <div key={i} style={{ padding: '4px 0', fontSize: '12px' }}>
            {i + 1}. {msg}
          </div>
        ))}
      </div>
    </div>
  );
};

export default MicrosoftCallback;
